import axios from "axios";
import type {
  BuyCreditsResult,
  CreditLot,
  CreditLotEvent,
  CreditPurchase,
  CreditSummary,
  CreditTransferResult,
  PaginatedTransfers,
  TransferAllocation,
  TransferableBalance,
} from "@/interfaces/credits";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's error message (not a bare axios error) so the UI can show it. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw error instanceof Error ? error : new Error(fallback);
}

/**
 * The wallet box: balance, expiring-soon, and the current rate.
 *
 * `until` asks the launch question instead — what a NEW campaign running to that date may actually
 * be funded with: the credits that outlive it, less the headroom the group's live campaigns already
 * hold. Without it, `available` is the free balance against today's lots.
 */
export async function fetchCreditSummary(
  groupId: number,
  until?: string | null,
): Promise<CreditSummary> {
  const { data } = await apiClient.get<{ success: boolean; data: CreditSummary }>(
    `/admin/groups/${groupId}/credits/summary`,
    until ? { params: { until } } : undefined,
  );
  return data.data;
}

/** An organization's total credit pool (Σ across its groups). */
export async function fetchOrgCreditBalance(orgId: number): Promise<string> {
  const { data } = await apiClient.get<{ success: boolean; data: { balance: string } }>(
    `/admin/organizations/${orgId}/credits/balance`,
  );
  return data.data.balance;
}

/** The group's credit lots. `all` = every state (incl. hold/reversed/depleted) for the Credits Pool
 *  management view; default = active-only (the balance-facing wallet). */
export async function fetchCreditLots(groupId: number, all = false): Promise<CreditLot[]> {
  const { data } = await apiClient.get<{ success: boolean; data: CreditLot[] }>(
    `/admin/groups/${groupId}/credits/lots`,
    { params: all ? { all: 1 } : undefined },
  );
  return data.data;
}

/** A lot's activity history (newest first) — the per-lot history dialog. */
export async function fetchCreditLotEvents(
  groupId: number,
  lotId: number,
): Promise<CreditLotEvent[]> {
  const { data } = await apiClient.get<{ success: boolean; data: CreditLotEvent[] }>(
    `/admin/groups/${groupId}/credits/lots/${lotId}/events`,
  );
  return data.data;
}

/** Manual Credits Pool actions (FINANCE). Hold/Reverse take a reason; Release takes none. */
export async function holdCreditLot(groupId: number, lotId: number, reason: string): Promise<void> {
  try {
    await apiClient.post(`/admin/groups/${groupId}/credits/lots/${lotId}/hold`, { reason });
  } catch (error) {
    rethrowApiError(error, "Couldn't hold the credit lot.");
  }
}

export async function releaseCreditLot(groupId: number, lotId: number): Promise<void> {
  try {
    await apiClient.post(`/admin/groups/${groupId}/credits/lots/${lotId}/release`, {});
  } catch (error) {
    rethrowApiError(error, "Couldn't release the credit lot.");
  }
}

export async function reverseCreditLot(
  groupId: number,
  lotId: number,
  reason: string,
): Promise<void> {
  try {
    await apiClient.post(`/admin/groups/${groupId}/credits/lots/${lotId}/reverse`, { reason });
  } catch (error) {
    rethrowApiError(error, "Couldn't reverse the credit lot.");
  }
}

/**
 * Buys credits on a saved card. Returns a purchaseId to poll — the lot is minted by the webhook,
 * not this request, so the balance moves a moment later.
 *
 * `causes` are the buyer's expiry beneficiaries, as variant ids in the order they were picked. Ids
 * only: the split is an even one by rule, so the server derives the shares. Omitting them is
 * legitimate — the quarter's default causes apply at mint instead.
 *
 * `credits` goes over the wire as a STRING, like every other credit amount in this API: they are
 * exact decimals in the database and a float cannot hold them faithfully.
 */
export async function buyCredits(
  groupId: number,
  credits: number,
  paymentMethodId: number,
  causes?: string[],
): Promise<BuyCreditsResult> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: BuyCreditsResult }>(
      `/admin/groups/${groupId}/credits/purchases`,
      {
        credits: String(credits),
        paymentMethodId,
        ...(causes && causes.length > 0 ? { causes } : {}),
      },
    );
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't start the purchase. Please try again.");
  }
}

/** A purchase's current status, for polling after Buy. */
export async function fetchCreditPurchase(
  groupId: number,
  purchaseId: number,
): Promise<CreditPurchase> {
  const { data } = await apiClient.get<{ success: boolean; data: CreditPurchase }>(
    `/admin/groups/${groupId}/credits/purchases/${purchaseId}`,
  );
  return data.data;
}

/** What this group may send, plus the sibling groups it may send to. */
export async function fetchTransferable(groupId: number): Promise<TransferableBalance> {
  const { data } = await apiClient.get<{ data: TransferableBalance }>(
    `/admin/groups/${groupId}/credits/transferable`,
  );
  return data.data;
}

/** One page of this group's transfer history, both directions. */
export async function fetchCreditTransfers(
  groupId: number,
  params: {
    search?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    limit?: number;
  } = {},
): Promise<PaginatedTransfers> {
  const { data } = await apiClient.get<PaginatedTransfers>(
    `/admin/groups/${groupId}/credits/transfers`,
    { params },
  );
  return data;
}

export async function transferCredits(
  groupId: number,
  destinationGroupId: number,
  allocations: TransferAllocation[],
  note?: string,
): Promise<CreditTransferResult> {
  const { data } = await apiClient.post<{ data: CreditTransferResult }>(
    `/admin/groups/${groupId}/credits/transfers`,
    { destinationGroupId, allocations, ...(note ? { note } : {}) },
  );
  return data.data;
}
